'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Check, Package } from 'lucide-react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import type { AssistantComparison } from '@/lib/parse-assistant-message';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import { priceByCountry } from '@/lib/ui-content/price-ui-content';
import { shelfCardDisplayPrice } from '@/lib/shelf-card-offers';
import { useNativeApp } from '@/components/runtime/native-app-context';
import { shouldSuppressAmazonLinks } from '@/lib/native/amazon-app-policy';
import { shouldBypassOptimizer } from '@/components/products/image-container/bypass-optimizer';
import { type FocusVisual, visualForComparisonItem } from '@/lib/focus-visuals';

const ROW_ENTER = 'fade-in-from-above 0.28s cubic-bezier(0.25,0.1,0.25,1) both';

// One grid template per item count, shared by every row so the columns line up.
// Phones: the aspect label sits on its own line above the values, which get the
// full width. From `sm`, the label takes a left column.
const GRID: Record<number, string> = {
  2: 'grid-cols-2 sm:grid-cols-[minmax(5.5rem,24%)_repeat(2,minmax(0,1fr))]',
  3: 'grid-cols-3 sm:grid-cols-[minmax(5.5rem,20%)_repeat(3,minmax(0,1fr))]',
};

interface ComparisonCardProps {
  comparison: AssistantComparison;
  locale: LocaleDetails;
  /** The named products as the catalogue has them (photo, price, link). */
  visuals?: FocusVisual[];
  onNavigate?: (url: string) => void;
}

/**
 * The visual answer to "what's the difference between X and Y": the products
 * side by side, the aspects that separate them, a ✓ on the better value, and
 * who each one suits.
 *
 * The rows are the conversation model's; the photos, prices and links are
 * ours — each column is matched by name to the catalogue product the focus
 * shelf found (lib/focus-visuals.ts), so the card and the shelf below it show
 * the same products at the same prices. A column the catalogue does not carry
 * keeps a neutral tile and no price: never an invented one.
 *
 * A grid rather than a <table> so the label can move above the values on a
 * phone; the ARIA table roles keep it a table for a screen reader ("Display,
 * iPad Air: LCD, 60 Hz"). Rows stream in as the model writes them.
 */
export function ComparisonCard({ comparison, locale, visuals = [], onNavigate }: ComparisonCardProps) {
  const ui = getSearchUiContent(locale);
  const { isAppOrigin } = useNativeApp();
  const suppressAmazon = shouldSuppressAmazonLinks(isAppOrigin);
  const { items, rows, bestFor } = comparison;
  const columns = items.map((item) => visualForComparisonItem(item, visuals));
  const grid = GRID[items.length] ?? GRID[2];
  const labelCell = 'col-span-full pb-0.5 pt-2 text-[11px] font-medium uppercase tracking-wide sm:col-span-1 sm:py-2 sm:pr-2 sm:text-xs sm:normal-case sm:tracking-normal';

  return (
    <div
      role="table"
      aria-label={ui.comparisonCaption ?? 'Side-by-side comparison'}
      className="w-full overflow-hidden rounded-xl border border-zinc-200/80 bg-white/80 text-[13px] leading-snug dark:border-white/10 dark:bg-white/[0.04]"
    >
      <div role="row" className={`grid ${grid} gap-x-3 px-3 pb-2.5 pt-3`}>
        <span role="columnheader" aria-hidden="true" className="hidden sm:block" />
        {items.map((item, i) => (
          <ItemHeader
            key={`${i}-${item}`}
            name={item}
            visual={columns[i]}
            locale={locale}
            suppressAmazon={suppressAmazon}
            stacked={items.length > 2}
            onNavigate={onNavigate}
          />
        ))}
      </div>

      {rows.map((row, r) => (
        <div
          key={`${r}-${row.label}`}
          role="row"
          className={`grid ${grid} gap-x-3 border-t border-zinc-100 px-3 pb-2 dark:border-white/[0.06] sm:pb-0`}
          style={{ animation: ROW_ENTER }}
        >
          <span role="rowheader" className={`${labelCell} text-zinc-400 dark:text-zinc-500 sm:text-zinc-500 sm:dark:text-zinc-400`}>
            {row.label}
          </span>
          {row.values.map((value, i) => {
            const better = row.edge === i && value.length > 0;
            return (
              <span
                key={i}
                role="cell"
                className={`min-w-0 break-words sm:py-2 ${
                  better ? 'font-medium text-zinc-900 dark:text-white' : 'text-zinc-600 dark:text-zinc-300'
                }`}
              >
                {better && (
                  <Check
                    aria-hidden="true"
                    strokeWidth={2.75}
                    className="-mt-0.5 mr-1 inline h-3.5 w-3.5 text-cornflowerblue"
                  />
                )}
                {value}
                {better && <span className="sr-only"> ({ui.comparisonAdvantage ?? 'Advantage'})</span>}
              </span>
            );
          })}
        </div>
      ))}

      {bestFor.length > 0 && (
        <div
          role="row"
          className={`grid ${grid} gap-x-3 border-t border-zinc-100 bg-cornflowerblue/[0.06] px-3 pb-2.5 dark:border-white/[0.06] dark:bg-cornflowerblue/[0.10] sm:pb-0`}
          style={{ animation: ROW_ENTER }}
        >
          <span role="rowheader" className={`${labelCell} font-semibold text-cornflowerblue`}>
            {ui.comparisonBestFor ?? 'Best for'}
          </span>
          {bestFor.map((text, i) => (
            <span key={i} role="cell" className="min-w-0 break-words text-zinc-800 dark:text-zinc-100 sm:py-2.5">
              {text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemHeader({
  name,
  visual,
  locale,
  suppressAmazon,
  stacked,
  onNavigate,
}: {
  name: string;
  visual: FocusVisual | undefined;
  locale: LocaleDetails;
  suppressAmazon: boolean;
  /** Three columns on a phone leave no room beside the photo: name goes under it. */
  stacked: boolean;
  onNavigate?: (url: string) => void;
}) {
  const image = visual?.product.image ?? null;
  const price = visual ? shelfCardDisplayPrice(visual.product, locale, suppressAmazon) : null;

  const body = (
    <span
      className={`flex min-w-0 gap-2 ${stacked ? 'flex-col items-start sm:flex-row sm:items-center' : 'items-center'}`}
    >
      <span
        className={`relative block h-10 w-10 shrink-0 overflow-hidden rounded-lg ring-1 ring-zinc-200/70 dark:ring-white/10 ${
          image ? 'bg-white' : 'bg-zinc-50 dark:bg-white/[0.05]'
        }`}
      >
        {image ? (
          <Image
            src={image}
            alt=""
            fill
            sizes="40px"
            className="object-contain p-0.5"
            unoptimized={shouldBypassOptimizer(image)}
          />
        ) : (
          <Package aria-hidden="true" className="absolute inset-0 m-auto h-4 w-4 text-zinc-300 dark:text-zinc-600" />
        )}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="line-clamp-2 text-[13px] font-semibold leading-tight text-zinc-900 transition-colors group-hover:text-cornflowerblue dark:text-white">
          {name}
        </span>
        {price != null && (
          <span className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
            {priceByCountry(price, locale.regionCode, locale.path)}
          </span>
        )}
      </span>
    </span>
  );

  if (!visual?.url) {
    return (
      <span role="columnheader" className="min-w-0">
        {body}
      </span>
    );
  }
  const url = visual.url;
  return (
    <span role="columnheader" className="min-w-0">
      <Link
        href={url}
        prefetch={false}
        onClick={() => onNavigate?.(url)}
        className="group block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-cornflowerblue/60"
      >
        {body}
      </Link>
    </span>
  );
}
